function resolveApiUrl() {
  try {
    // import.meta may be present in the Vite runtime. Use a safe any cast and optional chaining.
    // If import.meta or its env is not available (Jest), this may throw or be undefined — we'll fall back.
    // @ts-ignore
    const viteEndpoint = (import.meta as any)?.env?.VITE_GRAPHQL_ENDPOINT;
    if (viteEndpoint) {
      const resolved = viteEndpoint.replace("/graphql", "");
      console.log(
        "[AuthService] API_URL resolved from VITE_GRAPHQL_ENDPOINT:",
        {
          viteEndpoint,
          resolved,
        },
      );
      return resolved;
    }
  } catch (e) {
    // ignore and fallback to process.env or default
    console.debug("[AuthService] Error reading VITE_GRAPHQL_ENDPOINT:", e);
  }

  if (
    typeof process !== "undefined" &&
    process.env &&
    process.env.VITE_GRAPHQL_ENDPOINT
  ) {
    const resolved = process.env.VITE_GRAPHQL_ENDPOINT.replace("/graphql", "");
    console.log("[AuthService] API_URL resolved from process.env:", {
      endpoint: process.env.VITE_GRAPHQL_ENDPOINT,
      resolved,
    });
    return resolved;
  }

  console.warn("[AuthService] Using default API_URL: http://localhost:4000");
  return "http://localhost:4000";
}

const API_URL = resolveApiUrl();

export interface User {
  id: string;
  email: string;
  name: string;
  googleId?: string;
  googleEmail?: string;
  googlePicture?: string;
  provider?: string;
}

class AuthService {
  // With httpOnly cookies set by the backend we cannot access tokens from JS.
  // The frontend should rely on credentials: 'include' and server-side cookie checks.
  private inFlightGetCurrentUser: Promise<User | null> | null = null;
  private lastFetchedAt: number | null = null;
  // Minimum interval between authoritative /auth/me calls (ms)
  // Increased to reduce accidental hammering during startup/redirects
  private minFetchIntervalMs = 5000;

  async login(): Promise<void> {
    const loginUrl = `${API_URL}/auth/google`;
    console.log("[AuthService.login] DEBUG: Redirecting to Google OAuth", {
      loginUrl,
      timestamp: new Date().toISOString(),
    });
    // Redirect to backend Google OAuth endpoint
    window.location.href = loginUrl;
  }

  async logout(): Promise<void> {
    try {
      // Let the server read cookies (httpOnly) and revoke session server-side
      await fetch(`${API_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear any local storage
      localStorage.removeItem("user");
      // Redirect to login
      window.location.href = "/login";
    }
  }

  async getCurrentUser(force = false): Promise<User | null> {
    // Avoid hammering the backend by caching in-flight requests and respecting a minimum interval
    const now = Date.now();

    console.debug("[AuthService.getCurrentUser] Called", {
      force,
      hasInFlightRequest: !!this.inFlightGetCurrentUser,
      lastFetchedAt: this.lastFetchedAt,
      timeSinceLastFetch: this.lastFetchedAt
        ? now - this.lastFetchedAt
        : "never",
      minFetchIntervalMs: this.minFetchIntervalMs,
    });

    // If there's an in-flight request, reuse it
    if (this.inFlightGetCurrentUser) {
      console.debug("[AuthService] Reusing in-flight getCurrentUser request");
      return this.inFlightGetCurrentUser;
    }

    // If we fetched recently, return cached user from localStorage
    if (
      !force &&
      this.lastFetchedAt &&
      now - this.lastFetchedAt < this.minFetchIntervalMs
    ) {
      const stored = localStorage.getItem("user");
      console.debug(
        "[AuthService.getCurrentUser] Using cached user (within min interval)",
        {
          hasCachedUser: !!stored,
        },
      );
      if (!stored) return null;
      try {
        return JSON.parse(stored) as User;
      } catch (e) {
        // More neutral message — the stored value may be invalid JSON for many
        // reasons (manual edit, older format, partial write). Clear it and
        // return null.
        console.warn(
          "[AuthService] Invalid stored user data, clearing localStorage",
        );
        localStorage.removeItem("user");
        return null;
      }
    }

    // mark lastFetchedAt immediately to prevent a burst of callers
    // during redirects/startup — this is conservative and will be
    // overwritten with the real fetch time after success.
    this.lastFetchedAt = now;

    this.inFlightGetCurrentUser = (async () => {
      console.debug("[AuthService] Starting network getCurrentUser request", {
        url: `${API_URL}/auth/me`,
        credentialsMode: "include",
        documentCookies: document.cookie || "[no cookies]",
      });
      try {
        // Call /auth/me and rely on the httpOnly access_token cookie (credentials included)
        const response = await fetch(`${API_URL}/auth/me`, {
          method: "GET",
          credentials: "include",
        });

        console.debug("[AuthService] /auth/me response received", {
          status: response.status,
          ok: response.ok,
          statusText: response.statusText,
          contentType: response.headers.get("content-type"),
        });

        // Always capture response text first to debug
        let responseText = "";
        try {
          responseText = await response.clone().text();
        } catch (e) {
          responseText = "[Could not read response body]";
        }

        if (response.ok) {
          try {
            const user = await response.json();
            console.log("[AuthService] User authenticated successfully", {
              userId: user.id,
              userEmail: user.email,
              timestamp: new Date().toISOString(),
            });
            localStorage.setItem("user", JSON.stringify(user));
            this.lastFetchedAt = Date.now();
            return user as User;
          } catch (jsonError) {
            // Response is 200 but not valid JSON - something is wrong with the endpoint
            console.error(
              "[AuthService] ERROR: Response 200 but not JSON - endpoint returning HTML",
              {
                errorMessage:
                  jsonError instanceof Error
                    ? jsonError.message
                    : String(jsonError),
                responsePreview: responseText.substring(0, 500),
                contentType: response.headers.get("content-type"),
              },
            );
          }
        } else {
          console.warn("[AuthService] /auth/me returned non-ok status", {
            status: response.status,
            statusText: response.statusText,
            responsePreview: responseText.substring(0, 300),
          });
        }
      } catch (error) {
        console.error("Get current user error:", error, {
          errorMessage: error instanceof Error ? error.message : String(error),
        });
      } finally {
        // clear in-flight after completion so subsequent calls can retry
        this.inFlightGetCurrentUser = null;
      }

      this.lastFetchedAt = Date.now();
      console.debug("[AuthService] getCurrentUser returning null");
      return null;
    })();

    return this.inFlightGetCurrentUser;
  }

  async refreshToken(): Promise<string | null> {
    // Removed: frontend doesn't use a token-returning refresh flow because
    // the backend sets httpOnly cookies. The active refresh mechanism is the
    // `refreshTokens()` helper in `frontend/src/index.tsx` which POSTs to
    // `/auth/refresh` and relies on cookies. Kept the method previously for
    // backward compatibility, but it's unused at runtime and therefore removed
    // to avoid confusion.
    return null;
  }

  isAuthenticated(): boolean {
    // Synchronous check: rely on locally cached user presence. For authoritative state call getCurrentUser().
    const stored = localStorage.getItem("user");
    if (!stored) return false;
    try {
      JSON.parse(stored);
      return true;
    } catch (e) {
      // Invalid stored user data — treat as unauthenticated and clear it
      localStorage.removeItem("user");
      return false;
    }
  }
}

export const authService = new AuthService();

// Export the class for testing convenience (so tests can instantiate or mock it)
export default AuthService;
